import { useRef } from "react";

export type LastSettled<T> = {
  /** The most recently settled value, or the current one if nothing has settled yet. */
  value: T;
  /** Whether `value` has ever been settled. */
  hasSettled: boolean;
};

/**
 * Holds the last value that was settled, and remembers that it ever settled.
 *
 * For the answers that decide whether a whole tree renders at all. Those come from queries, and a
 * query goes back to loading long after the app is up — on a refetch, a reconnect, a request that
 * failed. Reading the raw value there does not show a spinner, it unmounts and remounts everything
 * below the gate, and it briefly answers "signed out" to a router that will act on it.
 *
 * `hasSettled` is the boot gate: false only until the first real answer arrives, true forever
 * after. `value` is that answer, held steady through every later reload.
 *
 * Deliberately a ref rather than state: the latch always moves on a render that already carries the
 * settled value, so there is nothing to schedule.
 */
export function useLastSettled<T>(value: T, isSettled: boolean): LastSettled<T> {
  const last = useRef<LastSettled<T>>({ value, hasSettled: false });

  if (isSettled) {
    last.current = { value, hasSettled: true };
  }

  return last.current;
}
